import RecordingExportOptionsModal from "@/components/RecordingExportOptionsModal";
import RecordingListNoResult from "@/components/RecordingListNoResult";
import RecordStatus from "@/components/RecordingStatus";
import WaitingModal from "@/components/WaitingModal";
import { useIndexDB } from "@/contexts/IndexDB/useIndexDB";
import { useRowSelection } from "@/contexts/RowSelection/useRowSelection";
import { useGetRecordsLibrary } from "@/hooks/queries/useGetRecordsLibrary";
import { RecordingHubMessage, RecordingHubPayload, useSignalRRecordingHub } from "@/hooks/useSignalRRecordingHub";
import { Export, Recording } from "@/models/recording";
import RecordingDurationCell from "@/pages/RecordsList/components/RecordingDurationCell";
import RecordingListHeader from "@/pages/RecordsList/components/RecordingListHeader";
import RecordingListSubTable from "@/pages/RecordsList/components/RecordingListSubTable";
import RecordingListTable from "@/pages/RecordsList/components/RecordingListTable";
import { type RecordItems } from "@/services/getRecordsLibrary";
import addIntervalToDate from "@/utils/addIntervalToDate";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RowTextStyled, TitleStyled } from "./components/RecordingListTable/RecordingListTable.styled";
import RecordStatusActions from "./components/RecordStatusActions";
import { mergeLocalRecordings } from "./helpers/mergeLocalRecordings";
import { RecordStatusEnum } from "./types";
import { Space, VerticalFlex } from "@enxcs/dap-ui-stratos";

type RecordingExportSignalRPayload = RecordingHubPayload & {
  recordingExportId: string;
  exportType: number;
  status: number;
  exportedDate?: string;
  date?: string;
};

const isRecordingExportSignalRMessage = (message: RecordingHubMessage): message is RecordingHubMessage & { data: RecordingExportSignalRPayload } => {
  return (
    (message.method === "RecordingExportCreated" || message.method === "RecordingExportUpdated") &&
    "recordingExportId" in message.data &&
    typeof message.data.recordingExportId === "string"
  );
};

const upsertExportFromSignalR = (record: Recording, payload: RecordingExportSignalRPayload): Recording => {
  if (record.recordingId !== payload.recordingId) {
    return record;
  }

  const currentExports = record.exports ?? [];
  const exportIndex = currentExports.findIndex((item) => item.id === payload.recordingExportId || item.recordingExportId === payload.recordingExportId);

  const previousExport = exportIndex >= 0 ? currentExports[exportIndex] : null;

  const mergedExport: Export = {
    id: previousExport?.id || payload.recordingExportId,
    recordingExportId: previousExport?.recordingExportId || payload.recordingExportId,
    fileName: previousExport?.fileName || "",
    recording: previousExport?.recording || "",
    recordingId: previousExport?.recordingId || payload.recordingId,
    exportType: payload.exportType,
    status: payload.status,
    exportedDate: payload.exportedDate || payload.date || previousExport?.exportedDate || "",
    exportSize: previousExport?.exportSize || 0,
  };

  if (exportIndex >= 0) {
    return {
      ...record,
      exports: currentExports.map((item, idx) => (idx === exportIndex ? mergedExport : item)),
    };
  }

  return {
    ...record,
    exports: [...currentExports, mergedExport],
  };
};

const RecordsList: FC = () => {
  const [exportModalRecordId, setExportModalRecordId] = useState("");
  const [showWaitingModal, setShowWaitingModal] = useState(false);
  const [triggerNewRecordingModal, setTriggerNewRecordingModal] = useState(false);
  const lastHandledMessageIdRef = useRef<string | null>(null);

  const { updateState, allRecordingData, deleteFromIndexDB } = useIndexDB();

  const { expandedRows, isSelectDisabled } = useRowSelection();

  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    data: recordsList = { items: [], currentPage: 1, pageSize: 25, totalItems: 0, totalPages: 0 },
    isLoading: isLoadingRecordsList,
    refetch: refetchRecordsList,
  } = useGetRecordsLibrary();

  const recordsListWithLocalItems = useMemo(() => mergeLocalRecordings(recordsList, allRecordingData), [recordsList, allRecordingData]);

  const { lastMessage, connect } = useSignalRRecordingHub();

  useEffect(() => {
    connect();
  }, [connect]);

  useEffect(() => {
    void updateState();
  }, [updateState]);

  useEffect(() => {
    if (!lastMessage) {
      return;
    }

    if (lastHandledMessageIdRef.current === lastMessage.id) {
      return;
    }

    lastHandledMessageIdRef.current = lastMessage.id;
    const isExportMessage = isRecordingExportSignalRMessage(lastMessage);

    if (!isExportMessage) {
      void Promise.all([refetchRecordsList(), updateState()]);
      return;
    }
    queryClient.setQueriesData<RecordItems>({ queryKey: ["recordingSubjects"] }, (existingData) => {
      if (!existingData) {
        return existingData;
      }

      const targetRecordIndex = existingData.items.findIndex((record) => record.recordingId === lastMessage.data.recordingId);

      if (targetRecordIndex === -1) {
        return existingData;
      }

      const currentRecord = existingData.items[targetRecordIndex];
      let nextRecord = currentRecord;

      if (isExportMessage) {
        nextRecord = upsertExportFromSignalR(nextRecord, lastMessage.data);
      }

      if (nextRecord === currentRecord) {
        return existingData;
      }

      const items = [...existingData.items];
      items[targetRecordIndex] = nextRecord;

      return {
        ...existingData,
        items,
      };
    });
  }, [lastMessage, queryClient, refetchRecordsList, updateState, allRecordingData, deleteFromIndexDB]);

  const handleTriggerNewRecordingModal = () => {
    setTriggerNewRecordingModal(true);
  };
  const readyStatuses = useMemo(
    () => new Set([RecordStatusEnum.DONE, RecordStatusEnum.READY_TO_EDIT, RecordStatusEnum.READY_TO_EXPORT, RecordStatusEnum.GENERATED]),
    [],
  );

  const tableStructureData = useMemo(
    () =>
      recordsListWithLocalItems?.items?.map((record: Recording) => {
        const isRecordingInProgress = record.status === RecordStatusEnum.RECORDING;

        return {
          key: record.recordingId,
          disabled: isSelectDisabled(record),
          cells: [
            { content: <TitleStyled> {record.title}</TitleStyled> },
            { content: <RowTextStyled> {addIntervalToDate(record.date, record.duration || "", t, record.status)}</RowTextStyled> },

            {
              content: <RecordingDurationCell recordingId={record.recordingId} staticDuration={record.duration || ""} isInProgress={isRecordingInProgress} />,
            },
            {
              content: <RecordStatus record={record} />,
              align: "flex-start" as const,
            },
            {
              content: <RecordStatusActions record={record} onOpenExportModal={(recordingId) => setExportModalRecordId(recordingId)} />,
            },
          ],
          subContent: readyStatuses.has(record.status)
            ? expandedRows.includes(record.recordingId) && (
                <RecordingListSubTable recordingId={record.recordingId} recordingTitle={record.title} exports={record.exports} />
              )
            : false,
        };
      }),
    [recordsListWithLocalItems.items, expandedRows, t, isSelectDisabled, readyStatuses],
  );

  return (
    <VerticalFlex $gap={Space.V8} $padBottom={"28px" as Space}>
      {exportModalRecordId && (
        <RecordingExportOptionsModal
          recordingId={exportModalRecordId}
          handleCloseModal={() => {
            setExportModalRecordId("");
          }}
          displayWaitingModal={() => setShowWaitingModal(true)}
          handleRefresh={() => refetchRecordsList()}
          exportTypes={
            recordsListWithLocalItems?.items
              .find((record) => record.recordingId === exportModalRecordId)
              ?.exports?.map((exportItem) => exportItem.exportType) || []
          }
        />
      )}

      <WaitingModal
        isOpen={showWaitingModal}
        handleClose={() => {
          setShowWaitingModal(false);
        }}
        handleConfirm={() => {
          setShowWaitingModal(false);
        }}
      />

      <RecordingListHeader triggerNewRecordingModal={triggerNewRecordingModal} setTriggerNewRecordingModal={setTriggerNewRecordingModal} />

      {recordsListWithLocalItems && !isLoadingRecordsList && recordsListWithLocalItems?.items?.length === 0 ? (
        <RecordingListNoResult handleTriggerNewRecordingModal={handleTriggerNewRecordingModal} />
      ) : (
        <RecordingListTable tableStructure={tableStructureData} />
      )}
    </VerticalFlex>
  );
};

export default RecordsList;
